import type {
	SerializedHeadingNode,
	SerializedHorizontalRuleNode,
	SerializedListNode,
	SerializedParagraphNode,
	SerializedTextNode,
} from '@payloadcms/richtext-lexical'
import type { SerializedLexicalNode } from '@payloadcms/richtext-lexical/lexical'
import type { JSXConverter } from '@payloadcms/richtext-lexical/react'
import { cva, cx } from 'cva'
import slug from 'slug'
import ContentBlockWrapper from '../molecules/ContentBlockWrapper'

export const headingVariants = cva({
	base: 'col-span-full tablet-l:col-span-10',
	variants: {
		tag: {
			h1: 'text-h1 text-blue font-bold',
			h2: 'text-h2 text-blue font-bold',
			h3: 'text-h3 font-bold',
			h4: 'text-h4 font-semibold',
			h5: 'text-h5 font-semibold',
			h6: 'text-h6 font-semibold',
		},
	},
})

const baseTextClasses = 'col-span-full tablet-l:col-span-8'

const isTextNode = (node: SerializedLexicalNode): node is SerializedTextNode => {
	return node.type === 'text'
}

const nodeHasChildren = (
	node: SerializedLexicalNode,
): node is SerializedLexicalNode & { children?: SerializedLexicalNode[] } => {
	return 'children' in node
}

function getTextContent(nodes: SerializedLexicalNode[]) {
	let textContent = ''
	for (const node of nodes) {
		if (isTextNode(node)) {
			textContent += node.text
		} else if (nodeHasChildren(node)) {
			textContent += getTextContent(node.children ?? [])
		}
	}
	return textContent
}

function isEmptyNode(node: SerializedParagraphNode) {
	return getTextContent([node]) === ''
}

export const HeadingConverter: JSXConverter<SerializedHeadingNode> = ({ node, nodesToJSX }) => {
	const textContent = getTextContent(node.children ?? [])
	if (textContent === '') return null

	const Tag = node.tag
	const id = slug(textContent)

	return (
		<ContentBlockWrapper blockTypes={['header']}>
			<Tag id={id} className={headingVariants({ tag: Tag })}>
				{nodesToJSX({ nodes: node.children })}
			</Tag>
		</ContentBlockWrapper>
	)
}

export const DisplayParagraphConverter: JSXConverter<SerializedParagraphNode> = ({
	node,
	nodesToJSX,
}) => {
	if (isEmptyNode(node)) return null
	return (
		<ContentBlockWrapper blockTypes={['component', 'text']}>
			<p className={cx('text-display', baseTextClasses)}>{nodesToJSX({ nodes: node.children })}</p>
		</ContentBlockWrapper>
	)
}

export const LargeParagraphConverter: JSXConverter<SerializedParagraphNode> = ({
	node,
	nodesToJSX,
}) => {
	if (isEmptyNode(node)) return null
	return (
		<ContentBlockWrapper blockTypes={['component', 'text']}>
			<p className={cx('text-body-lg', baseTextClasses)}>{nodesToJSX({ nodes: node.children })}</p>
		</ContentBlockWrapper>
	)
}

export const ParagraphConverter: JSXConverter<SerializedParagraphNode> = ({ node, nodesToJSX }) => {
	if (isEmptyNode(node)) return null
	return (
		<ContentBlockWrapper blockTypes={['component', 'text']}>
			<p className={cx('text-body-md', baseTextClasses)}>{nodesToJSX({ nodes: node.children })}</p>
		</ContentBlockWrapper>
	)
}

export const SmallParagraphConverter: JSXConverter<SerializedParagraphNode> = ({
	node,
	nodesToJSX,
}) => {
	if (isEmptyNode(node)) return null
	return (
		<ContentBlockWrapper blockTypes={['component', 'text']}>
			<p className={cx('text-body-sm', baseTextClasses)}>{nodesToJSX({ nodes: node.children })}</p>
		</ContentBlockWrapper>
	)
}

const listClassMap: Record<SerializedListNode['tag'], string> = {
	ul: 'list-outside list-circle space-y-sm text-body-md [&>li]:ps-5 [&>li]:relative',
	ol: 'list-outside list-decimal [&>li]:ml-4 space-y-sm text-body-md',
}

export const ListConverter: JSXConverter<SerializedListNode> = ({ node, nodesToJSX }) => {
	const { tag: Tag, start } = node
	const classes = listClassMap[Tag]
	return (
		<ContentBlockWrapper blockTypes={['component', 'text']}>
			<Tag className={cx(classes, baseTextClasses)} start={start}>
				{nodesToJSX({ nodes: node.children })}
			</Tag>
		</ContentBlockWrapper>
	)
}

export const HorizontalRuleConverter: JSXConverter<SerializedHorizontalRuleNode> = () => {
	return (
		<ContentBlockWrapper blockTypes={['component', 'text']}>
			<hr className="border-grey-20" />
		</ContentBlockWrapper>
	)
}
